/**
 * Claude Code Native Integration Generator
 *
 * Generates `.claude/` settings, hooks, and bin scripts when
 * `projections` includes `"claude"` in the component.yaml.
 *
 * Output layout:
 *   .claude/settings.json       — fileSuggestion + hook registrations
 *   .claude/hooks/*.sh          — Shell wrappers delegating to Node bin scripts
 *   .claude/bin/*               — Node entry scripts importing @aaac/runtime/claude-native
 *   .claude/cache/.gitkeep      — Cache directory
 *   CLAUDE.md (append block)    — Navigation-index integration docs
 */

import type { ComponentIR } from "../ir.js";
import type { GeneratedFile, GenerateOptions, Generator } from "./types.js";

// ---------------------------------------------------------------------------
// settings.json
// ---------------------------------------------------------------------------

function buildSettingsJson(): string {
  const settings = {
    fileSuggestion: {
      type: "command",
      command: "${CLAUDE_PROJECT_DIR}/.claude/bin/file-suggestion",
    },
    hooks: {
      SessionStart: [
        {
          type: "command",
          command: "${CLAUDE_PROJECT_DIR}/.claude/hooks/session-start.sh",
        },
      ],
      UserPromptSubmit: [
        {
          type: "command",
          command: "${CLAUDE_PROJECT_DIR}/.claude/hooks/user-prompt-submit.sh",
        },
      ],
      PreToolUse: [
        {
          type: "command",
          command: "${CLAUDE_PROJECT_DIR}/.claude/hooks/pre-tool-use.sh",
        },
      ],
    },
  };
  return JSON.stringify(settings, null, 2) + "\n";
}

// ---------------------------------------------------------------------------
// Hook shell wrappers
// ---------------------------------------------------------------------------

function buildHookScript(binName: string): string {
  return `#!/usr/bin/env bash
# Auto-generated by @aaac/contracts claude-generator. Do not edit.
# Delegates to the Node.js bin script for hook processing.
set -euo pipefail

SCRIPT_DIR="$(cd "$(dirname "\${BASH_SOURCE[0]}")" && pwd)"
BIN_DIR="$(dirname "$SCRIPT_DIR")/bin"

exec node "$BIN_DIR/${binName}" "$@"
`;
}

// ---------------------------------------------------------------------------
// Node.js bin scripts
// ---------------------------------------------------------------------------

function buildFileSuggestionBin(componentId: string): string {
  return `#!/usr/bin/env node
// Auto-generated by @aaac/contracts claude-generator. Do not edit.
// File suggestion handler for Claude Code @ completion.

import { readFileSync } from "node:fs";
import { join, dirname } from "node:path";
import { fileURLToPath } from "node:url";
import {
  createCacheStore,
  handleFileSuggestion,
} from "@aaac/runtime/claude-native";

const scriptDir = dirname(fileURLToPath(import.meta.url));
const cacheDir = join(scriptDir, "..", "cache");

async function main() {
  // Read prefix from stdin
  const chunks = [];
  for await (const chunk of process.stdin) {
    chunks.push(chunk);
  }
  const prefix = Buffer.concat(chunks).toString("utf-8").trim();

  // Load navigation index from cache
  const store = createCacheStore({ cacheDir });
  const entry = await store.read("navigation-index");
  if (!entry) {
    process.stderr.write("navigation-index cache not found. Run session-start first.\\n");
    process.exit(0);
  }

  const output = await handleFileSuggestion(entry.data, prefix);
  process.stdout.write(output);
}

main().catch((err) => {
  process.stderr.write(\`[${componentId}] file-suggestion error: \${err.message}\\n\`);
  process.exit(1);
});
`;
}

function buildEnsureNavigationIndexBin(
  componentId: string,
  configPath: string,
): string {
  return `#!/usr/bin/env node
// Auto-generated by @aaac/contracts claude-generator. Do not edit.
// Ensures navigation-index cache is warm and fresh.

import { join, dirname, resolve } from "node:path";
import { fileURLToPath } from "node:url";
import { ensureNavigationIndex } from "@aaac/runtime/claude-native";

const scriptDir = dirname(fileURLToPath(import.meta.url));
const projectRoot = resolve(scriptDir, "../..");
const cacheDir = join(scriptDir, "..", "cache");
const configFile = resolve(projectRoot, ${JSON.stringify(configPath)});

async function main() {
  const result = await ensureNavigationIndex({
    cacheDir,
    sourceFiles: [configFile],
    navigationOptions: {
      projectDslPath: configFile,
    },
  });

  const status = result.fromCache ? "cache-hit" : "rebuilt";
  process.stdout.write(JSON.stringify({ status, component: ${JSON.stringify(componentId)} }) + "\\n");
}

main().catch((err) => {
  process.stderr.write(\`[${componentId}] ensure-navigation-index error: \${err.message}\\n\`);
  process.exit(1);
});
`;
}

function buildSessionStartBin(
  componentId: string,
  configPath: string,
): string {
  return `#!/usr/bin/env node
// Auto-generated by @aaac/contracts claude-generator. Do not edit.
// SessionStart hook handler — cache warm + navigation-index summary injection.

import { join, dirname, resolve } from "node:path";
import { fileURLToPath } from "node:url";
import {
  readHookInput,
  emitHookOutput,
  createSessionStartHandler,
} from "@aaac/runtime/claude-native";

const scriptDir = dirname(fileURLToPath(import.meta.url));
const projectRoot = resolve(scriptDir, "../..");
const cacheDir = join(scriptDir, "..", "cache");
const configFile = resolve(projectRoot, ${JSON.stringify(configPath)});

async function main() {
  const input = await readHookInput();
  if (input.hookEventName !== "SessionStart") {
    process.exit(0);
  }

  const handler = createSessionStartHandler({
    cacheDir,
    sourceFiles: [configFile],
    navigationOptions: {
      projectDslPath: configFile,
    },
  });

  const output = await handler(input);
  emitHookOutput(output);
}

main().catch((err) => {
  process.stderr.write(\`[${componentId}] session-start error: \${err.message}\\n\`);
  process.exit(1);
});
`;
}

function buildContextPackBin(
  componentId: string,
  configPath: string,
): string {
  return `#!/usr/bin/env node
// Auto-generated by @aaac/contracts claude-generator. Do not edit.
// Generates ContextPack for UserPromptSubmit hook.

import { join, dirname, resolve } from "node:path";
import { fileURLToPath } from "node:url";
import {
  readHookInput,
  emitHookOutput,
  createUserPromptSubmitHandler,
} from "@aaac/runtime/claude-native";

const scriptDir = dirname(fileURLToPath(import.meta.url));
const projectRoot = resolve(scriptDir, "../..");
const cacheDir = join(scriptDir, "..", "cache");
const configFile = resolve(projectRoot, ${JSON.stringify(configPath)});

async function main() {
  const input = await readHookInput();
  if (input.hookEventName !== "UserPromptSubmit") {
    process.exit(0);
  }

  const handler = createUserPromptSubmitHandler({
    projectRoot,
    cacheDir,
    sourceFiles: [configFile],
    navigationOptions: {
      projectDslPath: configFile,
    },
  });

  const output = await handler(input);
  emitHookOutput(output);
}

main().catch((err) => {
  process.stderr.write(\`[${componentId}] build-contextpack error: \${err.message}\\n\`);
  process.exit(1);
});
`;
}

function buildCheckArtifactEditBin(
  componentId: string,
  configPath: string,
): string {
  return `#!/usr/bin/env node
// Auto-generated by @aaac/contracts claude-generator. Do not edit.
// PreToolUse artifact edit checker for Claude Code hooks.

import { join, dirname, resolve } from "node:path";
import { fileURLToPath } from "node:url";
import {
  readHookInput,
  emitHookOutput,
  createCacheStore,
  createPreToolUseHandler,
} from "@aaac/runtime/claude-native";

const scriptDir = dirname(fileURLToPath(import.meta.url));
const projectRoot = resolve(scriptDir, "../..");
const cacheDir = join(scriptDir, "..", "cache");

async function main() {
  const input = await readHookInput();
  if (input.hookEventName !== "PreToolUse") {
    process.exit(0);
  }

  // Load navigation index from cache
  const store = createCacheStore({ cacheDir });
  const entry = await store.read("navigation-index");
  if (!entry) {
    // No cache = no artifact protection; allow
    process.exit(0);
  }

  const handler = createPreToolUseHandler({
    navigationIndex: entry.data,
  });

  const output = await handler(input);
  emitHookOutput(output);
}

main().catch((err) => {
  process.stderr.write(\`[${componentId}] check-artifact-edit error: \${err.message}\\n\`);
  process.exit(1);
});
`;
}

// ---------------------------------------------------------------------------
// CLAUDE.md append block
// ---------------------------------------------------------------------------

function buildClaudeMdBlock(componentId: string): string {
  return `
<!-- aaac:claude-native:begin -->
## Navigation Index Integration (Auto-generated)

This project uses AaaC Navigation Index for Claude Code integration.
The \`.claude/\` directory is generated by \`aaac generate\` and provides:

- **File suggestions**: Artifact-aware \`@\` completion via \`.claude/bin/file-suggestion\`
- **Session context**: Navigation-index summary injected on session start
- **Prompt context**: ContextPack + candidate metadata on prompt submission
- **Edit guardrails**: Generated/protected artifact edit prevention

### Regenerate

\`\`\`bash
npx ${componentId} claude sync
\`\`\`

### Check for drift

\`\`\`bash
npx ${componentId} claude check
\`\`\`

Do not manually edit files in \`.claude/hooks/\` or \`.claude/bin/\` — they are regenerated by \`aaac generate\`.
<!-- aaac:claude-native:end -->
`;
}

// ---------------------------------------------------------------------------
// Generator
// ---------------------------------------------------------------------------

export const claudeGenerator: Generator = {
  generate(ir: ComponentIR, _options: GenerateOptions): GeneratedFile[] {
    if (!ir.projections.includes("claude")) {
      return [];
    }

    const componentId = ir.info.id;
    const configPath = ir.implementation ?? "agent-contracts.yaml";

    const files: GeneratedFile[] = [];

    // G-3: .claude/settings.json
    files.push({
      path: "settings.json",
      content: buildSettingsJson(),
      overwrite: true,
      targetDir: ".claude",
    });

    // G-4: .claude/hooks/*.sh
    files.push({
      path: "session-start.sh",
      content: buildHookScript("session-start"),
      overwrite: true,
      targetDir: ".claude/hooks",
    });
    files.push({
      path: "user-prompt-submit.sh",
      content: buildHookScript("build-contextpack"),
      overwrite: true,
      targetDir: ".claude/hooks",
    });
    files.push({
      path: "pre-tool-use.sh",
      content: buildHookScript("check-artifact-edit"),
      overwrite: true,
      targetDir: ".claude/hooks",
    });

    // G-5: .claude/bin/* Node entry scripts
    files.push({
      path: "file-suggestion",
      content: buildFileSuggestionBin(componentId),
      overwrite: true,
      targetDir: ".claude/bin",
    });
    files.push({
      path: "ensure-navigation-index",
      content: buildEnsureNavigationIndexBin(componentId, configPath),
      overwrite: true,
      targetDir: ".claude/bin",
    });
    files.push({
      path: "session-start",
      content: buildSessionStartBin(componentId, configPath),
      overwrite: true,
      targetDir: ".claude/bin",
    });
    files.push({
      path: "build-contextpack",
      content: buildContextPackBin(componentId, configPath),
      overwrite: true,
      targetDir: ".claude/bin",
    });
    files.push({
      path: "check-artifact-edit",
      content: buildCheckArtifactEditBin(componentId, configPath),
      overwrite: true,
      targetDir: ".claude/bin",
    });

    // G-6: .claude/cache/.gitkeep
    files.push({
      path: ".gitkeep",
      content: "# Cache directory for navigation-index and analyzer graph data.\n",
      overwrite: false,
      targetDir: ".claude/cache",
    });

    // G-7: CLAUDE.md append block
    files.push({
      path: "CLAUDE.md",
      content: buildClaudeMdBlock(componentId),
      overwrite: false,
      targetDir: ".",
    });

    return files;
  },
};
